/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useCallback, useEffect, useState } from "react";
import { Button } from "@vector-im/compound-web";
import { ErrorSolidIcon } from "@vector-im/compound-design-tokens/assets/web/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type TFunction } from "i18next";

import { useClientLegacy } from "../ClientContext";
import { Config } from "../config/Config";
import { E2eeType } from "../e2ee/e2eeType";
import { ErrorView } from "../ErrorView";
import { ErrorPage, FullScreenView, LoadingPage } from "../FullScreenView";
import { RoomAuthView } from "../room/RoomAuthView";
import { getRelativeRoomUrl } from "../utils/matrix";
import { widget } from "../widget";
import {
  applyInviteToken,
  getPublicInviteToken,
  InviteTokenRequestError,
  type PublicInviteTokenResponse,
} from "./inviteToken";

type RequestState<T> =
  | { kind: "loading" }
  | { kind: "ready"; value: T }
  | { kind: "error"; error: InviteTokenRequestError };

/** Маршрут проверки и применения gateway-токена приглашения. */
export const InviteTokenRoute: FC = () => {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const {
    client,
    loading: clientLoading,
    error: clientError,
  } = useClientLegacy();
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [applyAttempt, setApplyAttempt] = useState(0);
  const [tokenState, setTokenState] = useState<
    RequestState<PublicInviteTokenResponse>
  >({ kind: "loading" });
  const [applyError, setApplyError] = useState<InviteTokenRequestError>();

  useEffect(() => {
    let active = true;
    setTokenState({ kind: "loading" });

    const homeserver = Config.defaultHomeserverUrl();
    if (!homeserver || !token) {
      setTokenState({
        kind: "error",
        error: new InviteTokenRequestError(
          token ? "missing_homeserver" : "missing_token",
          400,
        ),
      });
      return;
    }

    void getPublicInviteToken(homeserver, token)
      .then((info) => {
        if (!active) return;
        setTokenState(
          info.is_active
            ? { kind: "ready", value: info }
            : {
                kind: "error",
                error: new InviteTokenRequestError("invalid", 410),
              },
        );
      })
      .catch((error: InviteTokenRequestError) => {
        if (active) setTokenState({ kind: "error", error });
      });

    return (): void => {
      active = false;
    };
  }, [token, validationAttempt]);

  useEffect(() => {
    if (!client || tokenState.kind !== "ready") return;

    let active = true;
    setApplyError(undefined);
    void applyInviteToken(client, token, tokenState.value.room_id)
      .then((result) => {
        if (!active) return;
        const room = client.getRoom(result.room_id);
        const encryptionSystem = {
          kind: room?.hasEncryptionStateEvent()
            ? E2eeType.PER_PARTICIPANT
            : E2eeType.NONE,
        } as const;
        void navigate(
          getRelativeRoomUrl(
            result.room_id,
            encryptionSystem,
            tokenState.value.room_name ?? undefined,
          ),
          { replace: true },
        );
      })
      .catch((error: InviteTokenRequestError) => {
        if (active) setApplyError(error);
      });

    return (): void => {
      active = false;
    };
  }, [applyAttempt, client, navigate, token, tokenState]);

  const retryValidation = useCallback(
    () => setValidationAttempt((attempt) => attempt + 1),
    [],
  );
  const retryApply = useCallback(
    () => setApplyAttempt((attempt) => attempt + 1),
    [],
  );

  if (tokenState.kind === "loading" || clientLoading) return <LoadingPage />;
  if (clientError) return <ErrorPage error={clientError} widget={widget} />;

  if (tokenState.kind === "error") {
    return (
      <InviteError
        error={tokenState.error}
        onRetry={tokenState.error.retryable ? retryValidation : undefined}
        t={t}
      />
    );
  }

  if (!client) return <RoomAuthView />;
  if (applyError) {
    return (
      <InviteError
        error={applyError}
        onRetry={applyError.retryable ? retryApply : undefined}
        t={t}
      />
    );
  }

  return <LoadingPage />;
};

const InviteError: FC<{
  error: InviteTokenRequestError;
  onRetry?: () => void;
  t: TFunction<"app">;
}> = ({ error, onRetry, t }) => (
  <FullScreenView>
    <ErrorView
      widget={widget}
      Icon={ErrorSolidIcon}
      title={
        error.invalid
          ? t("invite_token.invalid_title")
          : t("invite_token.error_title")
      }
    >
      <p>{getInviteErrorMessage(t, error.kind)}</p>
      {onRetry && (
        <Button kind="primary" onClick={onRetry}>
          {t("invite_token.retry")}
        </Button>
      )}
    </ErrorView>
  </FullScreenView>
);

function getInviteErrorMessage(
  t: TFunction<"app">,
  kind: InviteTokenRequestError["kind"],
): string {
  switch (kind) {
    case "invalid":
      return t("invite_token.invalid");
    case "service_unavailable":
      return t("invite_token.service_unavailable");
    case "validation_failed":
      return t("invite_token.validation_failed");
    case "apply_failed":
      return t("invite_token.apply_failed");
    case "missing_homeserver":
      return t("invite_token.missing_homeserver");
    case "missing_token":
      return t("invite_token.missing_token");
  }
}
